/**
 * Named standard grading scales (docgen E2, 2026-10-05; checker round 2 the same day).
 *
 * A protocol often says "adverse events are graded with NCI CTCAE version 5.0" and never prints the five
 * grades, because the scale is a published standard. That is a statement of the list, not an absence of one:
 * the grades are the standard's own. This module holds each recognised scale in ONE place, so another one
 * (a different published scale) is one more entry, nothing else.
 *
 * Precedence is decided by the callers, not here: a sponsor dictionary wins over everything, a list the
 * protocol actually PRINTS wins over a named scale, and a named scale wins over CDASH. A version is recorded
 * only when exactly one appears in the accepted sentence; it is never invented.
 *
 * A statement counts as THE adverse event severity scale only when it ties the scale to AE severity or grading
 * in general. It does not when it is negated ("CTCAE will not be used"), when it is about laboratory values,
 * dose changes, holds or infusion reactions, or when it defines a dose-limiting toxicity. Each sentence is
 * judged on its own.
 *
 * PURE. No Firestore, no model.
 */

export interface StandardScaleOption { code: number; label: string }

export interface StandardScale {
  id: string;
  /** How the scale is named in the source note, before the version. */
  display: string;
  /** The protocol text names the scale. */
  names: RegExp;
  options: StandardScaleOption[];
}

const GRADES = (words: string[]): StandardScaleOption[] => words.map((w, i) => ({ code: i + 1, label: `Grade ${i + 1} (${w})` }));

export const STANDARD_SEVERITY_SCALES: StandardScale[] = [
  {
    id: "ctcae",
    display: "NCI CTCAE",
    names: /\bCTCAE\b|Common\s+Terminology\s+Criteria\s+for\s+Adverse\s+Events/i,
    options: GRADES(["Mild", "Moderate", "Severe", "Life-threatening", "Death related to AE"]),
  },
  {
    id: "daids",
    display: "DAIDS AE Grading Table",
    names: /\bDAIDS\b(?=[^.]{0,80}(?:grad|table))|(?:grad|table)[^.]{0,80}\bDAIDS\b|Division\s+of\s+AIDS[^.]{0,80}(?:grad|table)/i,
    options: GRADES(["Mild", "Moderate", "Severe", "Potentially life-threatening", "Death"]),
  },
];

/** A statement about laboratory values, dose changes, holds or infusion reactions is not the AE severity scale. */
const NOT_AE_SCALE = /laborator|\blabs?\b|hematolog|haematolog|chemistry|urinalysis|abnormalit|neutropeni|\bdose[- ](?:modif\w*|reduc\w*|adjust\w*|delay\w*|change\w*|level\w*|escalat\w*)|\bholds?\b|interrupt|infusion/i;
/**
 * The sentence is ABOUT adverse events in general: it opens with them ("Adverse events, ...", "The severity of all
 * adverse events ...", "Severity of adverse reactions ..."). Only then may a lab, infusion or dose word sit inside
 * an "including ..." or "relationship to ..." clause without rejecting it.
 */
const AE_SUBJECT = /^(?:(?:the\s+)?(?:severity|intensity|grade|grading)\s+(?:of|for)\s+)?(?:all\s+|any\s+|each\s+|every\s+)?(?:adverse\s+(?:events?|reactions?)|AEs?|SAEs?|ADRs?|toxicit(?:y|ies))\b/i;
const SIDE_CLAUSES = /,?\s*including\b[^,;]*,?|\brelationship\s+to\b[^,;]*|\bcausality\b[^,;]*/gi;
/** A scale named to define dose-limiting toxicity is the definition of a DLT, not the AE severity list. */
const DLT = /dose[- ]limiting|\bDLTs?\b/i;
/** A negation that GOVERNS the scale: before its name ("is not graded per", "instead of", "never") or right after it ("will not be used", "is not applicable"). */
const NEGATION_BEFORE = /(?:\bnot\b(?!\s+(?:limited|only|just|all|every))|\bnever\b|\bno longer\b|\binstead of\b|\brather than\b|n't)[^.;]*$/i;
const NEGATION_AFTER = /^[\s)]*(?:\w+\s+){0,2}(?:(?:will|shall|would|do|does|is|are|be)\s+)*(?:not\s+(?:be\s+)?(?:used|applied|applicable|followed|use|apply)|never\s+(?:be\s+)?used)|^[\s)]*(?:\w+\s+){0,2}\w*n't\s+(?:be\s+)?(?:used|applied|apply|use)/i;
/** Inside a stored severity fact the topic is given; in free protocol text the sentence must tie the scale to AEs. */
const FACT_TIE = /grad|severity|intensity|adverse|\bAEs?\b|\bSAEs?\b|toxicity/i;
const TEXT_TIE = /(?:severity|intensity|grad(?:e|ed|ing))[^.]*\b(?:adverse\s+events?|AEs?|SAEs?)\b|\b(?:adverse\s+events?|AEs?|SAEs?)\b[^.]*(?:severity|intensity|grad(?:e|ed|ing))/i;

export interface NamedScale {
  scale: StandardScale;
  /** The version when exactly one is stated in the accepted sentence, else null. */
  version: string | null;
  /** More than one version appears in the accepted sentence: none is recorded. */
  multipleVersions: boolean;
  options: StandardScaleOption[];
  /** "NCI CTCAE v5.0", or "NCI CTCAE" when no single version is stated. */
  label: string;
}

const sentencesOf = (text: string): string[] =>
  String(text ?? "").replace(/\s+/g, " ").split(/(?<=[.!?;])\s+/).map((s) => s.trim()).filter(Boolean);

/** Every version stated in a sentence ("version 5.0", "v4.03", "Version 5", "CTCAE 4.03"), distinct, in order. */
function versionsIn(s: string): string[] {
  const out: string[] = [];
  const add = (v?: string) => { if (v && !out.includes(v)) out.push(v); };
  for (const m of s.matchAll(/\b(?:version|ver\.?|v\.?)\s*(\d+(?:\.\d+)?)(?!\d)/gi)) add(m[1]);
  for (const m of s.matchAll(/\bCTCAE\s*\)?\s*(\d+(?:\.\d+)?)(?!\d)/gi)) add(m[1]);
  return out;
}

/** Is the scale name in this sentence negated within about 40 characters? */
function negated(s: string, scale: StandardScale): boolean {
  const m = scale.names.exec(s);
  if (!m) return false;
  const before = s.slice(Math.max(0, m.index - 40), m.index);
  const after = s.slice(m.index + m[0].length, m.index + m[0].length + 40);
  return NEGATION_BEFORE.test(before) || NEGATION_AFTER.test(after);
}

function scaleOfSentence(s: string, strict: boolean): NamedScale | null {
  if (DLT.test(s)) return null;
  if (NOT_AE_SCALE.test(s.replace(SIDE_CLAUSES, " ")) && !AE_SUBJECT.test(s)) return null;
  if (!(strict ? TEXT_TIE : FACT_TIE).test(s)) return null;
  for (const scale of STANDARD_SEVERITY_SCALES) {
    if (!scale.names.test(s) || negated(s, scale)) continue;
    const versions = versionsIn(s);
    const version = versions.length === 1 ? versions[0] : null;
    return { scale, version, multipleVersions: versions.length > 1, options: scale.options.map((o) => ({ ...o })), label: `${scale.display}${version ? ` v${version}` : ""}` };
  }
  return null;
}

/** The recognised scale a severity statement names for adverse events, judged sentence by sentence, or null. */
export function namedSeverityScale(text: string): NamedScale | null {
  for (const s of sentencesOf(text)) {
    const n = scaleOfSentence(s, false);
    if (n) return n;
  }
  return null;
}

/** "Mild, moderate or severe" printed in a sentence (not a numbered grade list): the words in order, or null. */
export function printedSeverityWords(sentence: string): string[] | null {
  if (/grade\s*[1-5]/i.test(sentence)) return null;
  const words: string[] = [];
  for (const m of sentence.matchAll(/\b(mild|moderate|severe|life[- ]threatening|fatal|death)\b/gi)) {
    const w = m[1].toLowerCase().replace(/\s/, "-");
    if (!words.includes(w)) words.push(w);
  }
  return ["mild", "moderate", "severe"].every((w) => words.includes(w)) ? words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)) : null;
}

/** The printed severity words of the first sentence of a statement that lists them, or null. */
export function printedSeverityInStatement(text: string): string[] | null {
  for (const s of sentencesOf(text)) {
    const w = printedSeverityWords(s);
    if (w) return w;
  }
  return null;
}

/**
 * The first sentence of the protocol text that ties a standard scale to adverse event severity or grading, else
 * null (a sentence naming the scale only for labs, doses, holds, infusion reactions or a DLT does not count, and
 * neither does a text whose adverse event severity is printed as a list). Used only when the reading holds no
 * severity fact. A sentence that states exactly one version is preferred to one that states none.
 */
export function namedSeverityScaleInText(protocolText: string | null | undefined): { text: string; scale: NamedScale } | null {
  const sentences = sentencesOf(String(protocolText ?? ""));
  if (sentences.some((s) => TEXT_TIE.test(s) && !NOT_AE_SCALE.test(s) && printedSeverityWords(s))) return null;
  let first: { text: string; scale: NamedScale } | null = null;
  for (const s of sentences) {
    const scale = scaleOfSentence(s, true);
    if (!scale) continue;
    if (scale.version) return { text: s, scale };
    first ??= { text: s, scale };
  }
  return first;
}
